import { HelpDeskInputFieldEnum } from 'context/HelpDeskTooltipContext/helpDeskInputFieldEnum';
import { FormType } from 'enums/FieldTypeEnums';
import { Gutter } from 'antd/lib/grid/row';
import {
  Scope3CategoryConfig,
  Scope3EmissionUnit,
} from 'models/Scope3/scope3ManualEmission.model';

export const MANUAL_SCOPE3_CATEGORIES = [
  'category_2',
  'category_3',
  'category_7',
  'category_8',
  'category_10',
  'category_11',
  'category_12',
  'category_13',
  'category_14',
  'category_15',
] as const;

export type ManualScope3Category = typeof MANUAL_SCOPE3_CATEGORIES[number];

export function isManualScope3Category(
  category: string
): category is ManualScope3Category {
  return MANUAL_SCOPE3_CATEGORIES.includes(category as ManualScope3Category);
}

export const SCOPE3_EMISSION_UNIT: Record<
  Scope3EmissionUnit,
  Scope3EmissionUnit
> = {
  KgCo2e: 'KgCo2e',
  tCo2e: 'tCo2e',
};

export const SCOPE3_EMISSION_UNIT_OPTIONS = [
  { value: SCOPE3_EMISSION_UNIT.KgCo2e, label: 'kg CO₂e' },
  { value: SCOPE3_EMISSION_UNIT.tCo2e, label: 't CO₂e' },
];

export const MANUAL_SCOPE3_CATEGORY_CONFIG: Array<
  Scope3CategoryConfig & { id: ManualScope3Category }
> = [
  {
    id: 'category_2',
    label: 'Category 2 — Capital goods',
    helpdeskKey: HelpDeskInputFieldEnum.scope3Category2TotalEmissions,
    section: 'upstream',
    formType: 'manual_emission',
  },
  {
    id: 'category_3',
    label: 'Category 3 — Fuel and energy-related activities',
    helpdeskKey: HelpDeskInputFieldEnum.scope3Category3TotalEmissions,
    section: 'upstream',
    formType: 'manual_emission',
  },
  {
    id: 'category_7',
    label: 'Category 7 — Employee commuting',
    helpdeskKey: HelpDeskInputFieldEnum.scope3Category7TotalEmissions,
    section: 'upstream',
    formType: 'manual_emission',
  },
  {
    id: 'category_8',
    label: 'Category 8 — Upstream leased assets',
    helpdeskKey: HelpDeskInputFieldEnum.scope3Category8TotalEmissions,
    section: 'upstream',
    formType: 'manual_emission',
  },
  {
    id: 'category_10',
    label: 'Category 10 — Processing of sold products',
    helpdeskKey: HelpDeskInputFieldEnum.scope3Category10TotalEmissions,
    section: 'downstream',
    formType: 'manual_emission',
  },
  {
    id: 'category_11',
    label: 'Category 11 — Use of sold products',
    helpdeskKey: HelpDeskInputFieldEnum.scope3Category11TotalEmissions,
    section: 'downstream',
    formType: 'manual_emission',
  },
  {
    id: 'category_12',
    label: 'Category 12 — End-of-life treatment of sold products',
    helpdeskKey: HelpDeskInputFieldEnum.scope3Category12TotalEmissions,
    section: 'downstream',
    formType: 'manual_emission',
  },
  {
    id: 'category_13',
    label: 'Category 13 — Downstream leased assets',
    helpdeskKey: HelpDeskInputFieldEnum.scope3Category13TotalEmissions,
    section: 'downstream',
    formType: 'manual_emission',
  },
  {
    id: 'category_14',
    label: 'Category 14 — Franchises',
    helpdeskKey: HelpDeskInputFieldEnum.scope3Category14TotalEmissions,
    section: 'downstream',
    formType: 'manual_emission',
  },
  {
    id: 'category_15',
    label: 'Category 15 — Investments',
    helpdeskKey: HelpDeskInputFieldEnum.scope3Category15TotalEmissions,
    section: 'downstream',
    formType: 'manual_emission',
  },
];

export function getManualScope3CategoryConfig(
  category: string
): Scope3CategoryConfig | undefined {
  if (!isManualScope3Category(category)) return undefined;
  return MANUAL_SCOPE3_CATEGORY_CONFIG.find(config => config.id === category);
}

export const MANUAL_SCOPE3_CONSTANTS = {
  LABELS: {
    UNIT: 'Unit',
    TOTAL_EMISSIONS: 'Total emissions',
  },
  FIELD_UNIT: 'unit',
  FIELD_EMISSIONS: 'emissions',
  VALIDATION: {
    UNIT_INVALID: 'Select kg CO₂e or t CO₂e',
    EMISSIONS_NEGATIVE: 'Emissions cannot be negative',
    EMISSIONS_DECIMALS: 'Enter a number with a maximum of 4 decimal places',
  },
  REPORTING_HINT_PREFIX: '≈',
  REPORTING_HINT_SUFFIX: 't CO₂e for reporting',
  rowGutter: [16, 16] as Gutter,
  formType: FormType.REACT_HOOK_FORMS,
};
